import { useRef, useState } from "react";
import { Share2, Download, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ShareableAchievementProps {
  type: "streak" | "levelup" | "badge" | "graduation" | "milestone";
  title: string;
  subtitle?: string;
  value: string | number;
  emoji: string;
  userName?: string;
}

export default function ShareableAchievement({ type, title, subtitle, value, emoji, userName }: ShareableAchievementProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [shared, setShared] = useState(false);
  const { toast } = useToast();

  const generateImage = async (): Promise<Blob | null> => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const w = 600;
    const h = 400;
    canvas.width = w;
    canvas.height = h;

    const gradient = ctx.createLinearGradient(0, 0, w, h);
    gradient.addColorStop(0, "#1a1612");
    gradient.addColorStop(1, "#0f0d0a");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = "rgba(202, 158, 50, 0.3)";
    ctx.lineWidth = 3;
    ctx.strokeRect(20, 20, w - 40, h - 40);

    const cornerSize = 30;
    ctx.strokeStyle = "#ca9e32";
    ctx.lineWidth = 2;
    [[20, 20, 1, 1], [w - 20, 20, -1, 1], [20, h - 20, 1, -1], [w - 20, h - 20, -1, -1]].forEach(([x, y, dx, dy]) => {
      ctx.beginPath();
      ctx.moveTo(x as number, (y as number) + (dy as number) * cornerSize);
      ctx.lineTo(x as number, y as number);
      ctx.lineTo((x as number) + (dx as number) * cornerSize, y as number);
      ctx.stroke();
    });

    ctx.font = "48px serif";
    ctx.textAlign = "center";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(emoji, w / 2, 120);

    ctx.font = "bold 32px sans-serif";
    ctx.fillStyle = "#ca9e32";
    ctx.fillText(String(value), w / 2, 175);

    ctx.font = "bold 22px sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(title, w / 2, 220);

    if (subtitle) {
      ctx.font = "16px sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      const lines = wrapText(ctx, subtitle, w - 100);
      lines.forEach((line, i) => {
        ctx.fillText(line, w / 2, 255 + i * 22);
      });
    }

    if (userName) {
      ctx.font = "14px sans-serif";
      ctx.fillStyle = "rgba(202, 158, 50, 0.7)";
      ctx.fillText(userName, w / 2, h - 60);
    }

    ctx.font = "bold 14px sans-serif";
    ctx.fillStyle = "rgba(202, 158, 50, 0.5)";
    ctx.fillText("TrybeUP™", w / 2, h - 35);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/png");
    });
  };

  const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const words = text.split(" ");
    const lines: string[] = [];
    let current = "";
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth) {
        if (current) lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  };

  const handleShare = async () => {
    const blob = await generateImage();
    if (!blob) return;

    const file = new File([blob], "trybeup-achievement.png", { type: "image/png" });
    const shareText = `${emoji} ${title} — ${value}${subtitle ? ` | ${subtitle}` : ""}`;

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          title: "My TrybeUP Achievement",
          text: shareText,
          files: [file],
        });
        setShared(true);
        setTimeout(() => setShared(false), 2000);
        return;
      } catch {
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "trybeup-achievement.png";
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded!", description: "Achievement card saved to your device." });
    setShared(true);
    setTimeout(() => setShared(false), 2000);
  };

  return (
    <>
      <canvas ref={canvasRef} className="hidden" />
      <button
        onClick={handleShare}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
        aria-label="Share achievement"
      >
        {shared ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
        {shared ? "Shared!" : "Share"}
      </button>
    </>
  );
}
